/**
 * Job detail island loader: hydrates the server-rendered tree in place (indexable-page hydration
 * rework, following the SRP fix). layouts/LayoutDefault.tsx's JobRouteContent now server-renders
 * JobDetailPage wrapped in the SAME full provider stack this file builds (IntlProvider,
 * AuthProvider, UserProvider, FeedbackModalProvider, ToastProvider, JobIslandProvider) directly
 * into #job-ssr — there's no separate #job-app to mount a new tree into and swap in anymore, so
 * this file just calls hydrateRoot() on #job-ssr once the JobDetailPage chunk is ready. That
 * removes the old two-tree swap (and the "render hidden, then re-render visible so charts paint"
 * workaround it needed) entirely: content is never hidden in the first place, so there's nothing
 * for a chart-measuring-its-container bug to trip on.
 */
import React from 'react';
import { hydrateRoot } from 'react-dom/client';
import { getReactRootSentryOptions } from '../../utils/react/react-root-sentry-options';
import type { Root } from 'react-dom/client';
import { Provider } from 'react-redux';
import { IntlProvider } from 'react-intl';
import { loadLocaleData } from '../../utils/routing/url-helpers';
import { buildFooterStaticStrings } from '../../utils/footer/buildFooterStaticStrings';
import { createStore } from '../../redux/createStore';
import { AuthProvider } from '../../contexts/AuthContext';
import { ExperimentProvider } from '../../contexts/ExperimentContext';
import { UserProvider } from '../../query/UserProvider';
import { FeedbackModalProvider } from '../../contexts/FeedbackModalContext';
import { ToastProvider } from '../../contexts/ToastContext';
import { FooterStringsProvider } from '../../contexts/FooterStringsContext';
import { ToastContainer } from '../../design-system/Toast';
import { JobIslandProvider } from '../../contexts/JobIslandContext';
import { MinimalChrome } from '../../layouts/MinimalChrome';
import {
  scheduleIdleCallbackWithFallback,
  cancelIdleScheduleHandle,
  type IdleScheduleHandle,
} from '../../utils/browser/scheduleIdleCallbackWithFallback';
import type { PageContextClient } from 'vike/types';
import type { Data } from './@jobId/@seoSlug/+data';

/**
 * Worst-case cap (ms) passed to requestIdleCallback's `timeout` for the deferred first-load mount
 * path. NOT a fixed delay — see srp.client.ts's constant of the same name for the full reasoning.
 * Deferring hydration has no LCP cost (hydrating in place never repaints already-correct content),
 * so this only trades off how soon the page becomes interactive, same as before.
 */
const DEFER_MOUNT_FALLBACK_MS = 2500;

/**
 * Kicks off the JobDetailPage chunk download. Idempotent: safe to call more than once (import()
 * itself caches the module by specifier), memoized here so mountJobIsland can await the same
 * in-flight promise instead of a fresh one.
 */
let jobDetailPagePrefetchPromise: Promise<typeof import('./@jobId/@seoSlug/+Page')> | null = null;
function prefetchJobDetailPage(): void {
  if (jobDetailPagePrefetchPromise) return;
  jobDetailPagePrefetchPromise = import('./@jobId/@seoSlug/+Page');
}

let jobRoot: Root | null = null;
let jobRootContainer: HTMLElement | null = null;

function getLocaleFromPathname(pathname: string): string {
  const first = pathname.split('/').filter(Boolean)[0]?.toLowerCase();
  return first && ['en', 'fi', 'sv'].includes(first) ? first : 'en';
}

/** Parsed payload: either legacy (data only) or { data, userState } for island hydration. */
interface JobInitialPayload {
  data?: Data;
  userState?: Record<string, unknown>;
  job?: unknown;
}

function parseJobInitial(scriptEl: HTMLScriptElement): { jobData: Data | null; userState: Record<string, unknown> } {
  try {
    const raw = scriptEl.textContent ?? '';
    const parsed = JSON.parse(raw) as JobInitialPayload;
    if (!parsed || typeof parsed !== 'object') {
      return { jobData: null, userState: {} };
    }
    // New shape: { data, userState }
    if (parsed.data && typeof parsed.data === 'object') {
      const data = parsed.data as Data;
      return {
        jobData: data,
        userState: (parsed.userState && typeof parsed.userState === 'object' ? parsed.userState : {}) as Record<string, unknown>,
      };
    }
    // Legacy shape: raw job data (data at top level with .job)
    const asData = parsed as unknown as Data;
    if (asData?.job) {
      return { jobData: asData, userState: {} };
    }
    return { jobData: null, userState: {} };
  } catch {
    return { jobData: null, userState: {} };
  }
}

/**
 * Unmount the job island root synchronously. Must be called by `+onRenderClient.ts` before it
 * creates/renders a *different* React root over `#root` (an ancestor of `#job-ssr`) — see the
 * matching `unmountSrpIsland` in `srp.client.ts` for why this ordering matters.
 */
export function unmountJobIsland(): void {
  if (jobRoot) {
    jobRoot.unmount();
    jobRoot = null;
    jobRootContainer = null;
  }
}

/**
 * Parses #job-initial and hydrates #job-ssr in place. Deferred behind an idle callback by
 * runJobIslandLoader below unless mountImmediately is set — see that function for why.
 */
async function mountJobIsland(pageContext: PageContextClient): Promise<void> {
  const container = document.getElementById('job-ssr');
  const scriptEl = document.getElementById('job-initial');
  if (!container) return;

  const parsed =
    scriptEl && scriptEl.tagName === 'SCRIPT'
      ? parseJobInitial(scriptEl as HTMLScriptElement)
      : { jobData: null, userState: {} };
  const jobData = parsed.jobData;
  if (!jobData?.job) return;

  const pc = pageContext as { userState?: Record<string, unknown> } | undefined;
  const effectiveUserState =
    Object.keys(parsed.userState).length > 0
      ? parsed.userState
      : pc?.userState && typeof pc.userState === 'object'
        ? pc.userState
        : {};

  const pathname =
    typeof window !== 'undefined' ? window.location.pathname : ((pageContext as any)?.urlPathname ?? '/');
  const locale = (pageContext?.locale as string) || getLocaleFromPathname(pathname) || 'en';
  const urlOriginal = typeof window !== 'undefined' ? window.location.href : pathname;
  const messages = loadLocaleData(locale);
  const footerStrings = buildFooterStaticStrings(messages);
  const store = createStore({
    ...pageContext,
    data: jobData,
    userState: effectiveUserState,
  } as PageContextClient);
  const jobIslandValue = {
    locale,
    pathname,
    urlOriginal,
    user: (pageContext?.user ?? null) as any,
  };
  const initialUser = (pageContext?.user ?? null) as any;
  const initialHasBackofficeAccess = Boolean((pageContext as { hasBackofficeAccess?: boolean })?.hasBackofficeAccess);
  // TEC-508: this island builds its own tree by hand and never wraps it in vike-react's page-
  // context provider (see ExperimentContext.tsx's doc comment) — explicit-prop threading here,
  // same as initialUser above, not usePageContext() inside a leaf component.
  const initialExperiments = (pageContext as { experiments?: Record<string, string> })?.experiments ?? {};

  prefetchJobDetailPage();
  const { default: JobDetailPage } = await jobDetailPagePrefetchPromise!;

  const tree = React.createElement(
    Provider,
    { store },
    React.createElement(
      IntlProvider,
      { messages, locale, defaultLocale: 'en' },
      React.createElement(
        FooterStringsProvider,
        { strings: footerStrings },
        React.createElement(
          AuthProvider,
          { initialUser, initialHasBackofficeAccess },
          React.createElement(
            ExperimentProvider,
            { initialVariants: initialExperiments },
          React.createElement(
            UserProvider,
            { initialUser, initialHasBackofficeAccess, initialUserState: effectiveUserState as any },
            React.createElement(
              FeedbackModalProvider,
              null,
              React.createElement(
                ToastProvider,
                null,
                React.createElement(
                  React.Fragment,
                  null,
                  React.createElement(ToastContainer),
                  React.createElement(
                    JobIslandProvider,
                    { value: jobIslandValue },
                    React.createElement(
                      MinimalChrome,
                      null,
                      React.createElement(JobDetailPage, { initialData: jobData, initialLocale: locale }),
                    ),
                  ),
                ),
              ),
            ),
          ),
          ),
        ),
      ),
    ),
  );

  if (jobRoot && jobRootContainer === container) {
    jobRoot.render(tree);
    return;
  }

  if (jobRoot && jobRootContainer) {
    jobRoot.unmount();
    jobRoot = null;
    jobRootContainer = null;
  }

  // hydrateRoot, not createRoot: LayoutDefault.tsx's JobRouteContent already server-rendered this
  // exact tree into `container` — this attaches event listeners to the existing DOM instead of
  // building and painting a new one. getReactRootSentryOptions() wires onRecoverableError to
  // Sentry, so any hydration mismatch shows up as real production telemetry.
  jobRoot = hydrateRoot(container, tree, getReactRootSentryOptions());
  jobRootContainer = container;
}

export interface RunJobIslandLoaderOptions {
  /**
   * Default true (unlike SRP's island, which defaults false) so every existing caller keeps its
   * current eager-mount behavior unless it explicitly opts into deferring — only
   * pages/+onRenderClient.ts's fresh-document-load call site does that (see the comment there).
   */
  mountImmediately?: boolean;
}

/**
 * Entry point: defers the real mount (mountJobIsland above) behind an idle callback unless
 * mountImmediately is set. Deferring has no LCP cost post-hydration-fix (see DEFER_MOUNT_FALLBACK_MS
 * above) — it only affects how soon the page becomes interactive, trading that against not
 * spending main-thread time on visits that never interact before navigating away.
 */
export function runJobIslandLoader(
  pageContext: PageContextClient,
  options: RunJobIslandLoaderOptions = {},
): void {
  const mountImmediately = options.mountImmediately ?? true;

  let mounted = false;
  let fallbackHandle: IdleScheduleHandle | null = null;

  const doMount = (): void => {
    if (mounted) return;
    mounted = true;
    if (fallbackHandle !== null) {
      cancelIdleScheduleHandle(fallbackHandle);
      fallbackHandle = null;
    }
    void mountJobIsland(pageContext);
  };

  if (mountImmediately) {
    doMount();
    return;
  }

  const onInteraction = (): void => {
    doMount();
    document.removeEventListener('pointerdown', onInteraction);
    document.removeEventListener('keydown', onInteraction);
    document.removeEventListener('focus', onInteraction, true);
  };

  document.addEventListener('pointerdown', onInteraction, { once: true, passive: true });
  document.addEventListener('keydown', onInteraction, { once: true, passive: true });
  document.addEventListener('focus', onInteraction, { once: true, capture: true });

  fallbackHandle = scheduleIdleCallbackWithFallback(doMount, { timeout: DEFER_MOUNT_FALLBACK_MS });
}
